import MainCard from '@/components/MainCard';
import React, { useState } from "react";
import {
  Steps,
  Descriptions,
  Table,
  Empty,
  Timeline,
  Button,
  Badge,
  Modal,
  Tooltip,
  Tag,
} from "antd";
import { Download, ArrowLeft, Eye, MessageCircle } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { get_batch_report_pdf } from '@/utils/api';
import { base64ToFile } from '@/pages/reusable_functions';
import { RightOutlined, DownOutlined } from "@ant-design/icons";
import "../.././assets/styles/expand.css";
import { defaultColors } from '@/themes/antd-theme';
import { formatDateTime } from '@/utils/datetime';

const deviation_color = (per) => {
  let color;
  let bg_color;

  switch (true) {
    case per >= 0 && per <= 5:
      color = "green";
      bg_color = "#CCFFCC ";
      break;
    case (per >= -10 && per < 0) || (per > 5 && per <= 50):
      color = "orange";
      bg_color = "#FFF59E";
      break;
    case per < -10 || per > 50:
      color = "red";
      bg_color = "#FFCCCC";
      break;
    default:
      color = "black";
  }
  return { color: color, bg_color: bg_color };
};

function ReportSkeleton({ report }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const report2 = state?.report;
  const batch_number = state?.redirect_batch;

  const expandedRowRender = (record) => {
    let detailsRemarks = record?.detailsRemarks;
    return (
      <div className="space-y-2">
        {detailsRemarks?.batch_ingredients?.length > 0 && (
          <>
            <Table
              title={() => (
                <h5 className="text-lg font-semibold">Batch Ingredients</h5>
              )}
              dataSource={detailsRemarks?.batch_ingredients}
              size="small"
              columns={[
                {
                  title: "Date-Time",
                  dataIndex: "created_on",
                  key: "created_on",
                  render: (created_on) => formatDateTime(created_on),
                  width: "15%",
                },
                {
                  title: "Ingredient",
                  dataIndex: "ingredients_name",
                  key: "ingredients_name",
                  render: (_, record) =>
                    `${record?.ingredients_name} (${record?.unit})`,
                  width: "15%",
                },
                {
                  title: "Lot No.",
                  dataIndex: "lot_number",
                  key: "lot_number",
                  width: "15%",
                },
                {
                  title: "Expected ",
                  dataIndex: "expected_quantity",
                  key: "expected_quantity",
                  width: "15%",
                },
                {
                  title: "Actual",
                  dataIndex: "quantity",
                  key: "quantity",
                  width: "15%",
                },
                {
                  title: "Deviation",
                  dataIndex: "quantity_deviation",
                  key: "quantity_deviation",
                  width: "15%",
                  render: (_, record) => record?.quantity_deviation + " %",
                  onCell: (record) => ({
                    style: {
                      backgroundColor: deviation_color(
                        record?.quantity_deviation
                      )["bg_color"],
                      color: deviation_color(record?.quantity_deviation)[
                        "color"
                      ],
                    },
                  }),
                },
                {
                  title: "Temperature(°C)",
                  dataIndex: "temperature",
                  key: "temperature",
                  width: "15%",
                  align:"center",
                  render: (value, row, index) => {
                    // Calculate row span based on some condition or grouping
                    const sameTemperatureItems = detailsRemarks?.batch_ingredients?.filter(item => item.temperature === value);
                    const rowSpan = index === detailsRemarks?.batch_ingredients?.findIndex(item => item.temperature === value) ? sameTemperatureItems.length : 0;
                
                    return {
                      children: value,
                      props: {
                        rowSpan
                      }
                    };
                  }
                }
              ]}
              pagination={false}
              bordered
            />
          </>
        )}

        {/* {detailsRemarks?.machine_parameters_value?.length > 0 && (
        <>
          <h5 className="text-lg font-semibold mb-2">Machines</h5>
          <Descriptions
            column={1}
            size="small"
            bordered
            items={[
              {
                key: 1,
                label: "Machines",
                children: detailsRemarks?.machine_parameters_value?.map(
                  (item) => (
                    <Chip
                      size="small"
                      style={{ margin: 4 }}
                      label={item?.equipment}
                    />
                  )
                ),
              },
            ]}
          />
        </>
      )} */}

        {/* {detailsRemarks?.sampling?.length > 0 && (
        <>
          <h5 className="text-lg font-semibold mb-2">Sampling</h5>
          <Table
            dataSource={detailsRemarks?.sampling}
            size="small"
            columns={[
              {
                title: "Material",
                dataIndex: "material_inventory_name",
                key: "material_inventory_name",
              },
              {
                title: "Testing Type",
                dataIndex: "testing_type_name",
                key: "testing_type_name",
              },
              {
                title: "Quantity",
                dataIndex: "quantity",
                key: "quantity",
                render: (_, record) =>
                  `${record?.quantity} ${record?.material_inventory_unit}`,
              },
            ]}
            pagination={false}
            bordered
          />
        </>
      )} */}

        {detailsRemarks?.batch_sample_value?.length > 0 && (
          <>
            <Table
              title={() => <h5 className="text-lg font-semibold">Sampling</h5>}
              dataSource={detailsRemarks?.batch_sample_value}
              size="small"
              columns={[
                {
                  title: "Ingredients",
                  dataIndex: "material_inventory_name",
                  key: "ingredients",
                  render: (_, record) =>
                    `${record?.material_inventory_name} (${record?.material_inventory_unit})`,
                },
                {
                  title: "Testing Type",
                  dataIndex: "testing_type_name",
                  key: "testing_type",
                },
                {
                  title: "Sample Number",
                  dataIndex: "sample_number",
                  key: "sample_number",
                },
                {
                  title: "Date",
                  dataIndex: "created_on",
                  key: "created_on",
                  render: (created_on) => formatDateTime(created_on),
                },
                {
                  title: "Quantity",
                  dataIndex: "quantity",
                  key: "quantity",
                },
              ]}
              pagination={false}
              bordered
            />
          </>
        )}

        {detailsRemarks?.batch_parameters?.length > 0 && (
          <>
            <Table
              title={() => (
                <h5 className="text-lg font-semibold">Monitoring Parameters</h5>
              )}
              dataSource={detailsRemarks?.batch_parameters}
              size="small"
              columns={[
                {
                  title: "Date-Time",
                  dataIndex: "created_on",
                  key: "created_on",
                  render: (created_on) => formatDateTime(created_on),
                },
                {
                  title: "Parameter",
                  dataIndex: "parameter_name",
                  key: "parameter_name",
                  render: (_, record) =>
                    `${record?.parameter_name} (${record?.parameter_unit})`,
                },
                {
                  title: "Expected value",
                  dataIndex: "quantity",
                  key: "quantity",
                },
                {
                  title: "Min",
                  dataIndex: "min_value",
                  key: "min_value",
                },
                {
                  title: "Max",
                  dataIndex: "max_value",
                  key: "max_value",
                },
                {
                  title: "Actual Value",
                  dataIndex: "value",
                  key: "value",
                },
                {
                  title: "Deviation",
                  dataIndex: "deviation",
                  key: "deviation",
                  width: "20%",
                  render: (_, record) => record?.deviation + " %",
                  onCell: (record) => ({
                    style: {
                      backgroundColor: deviation_color(record?.deviation)[
                        "bg_color"
                      ],
                      color: deviation_color(record?.deviation)["color"],
                    },
                  }),
                },
              ]}
              pagination={false}
              bordered
            />
          </>
        )}
        {/* 
      {detailsRemarks?.process_products?.length > 0 && (
        <>
          <h5 className="text-lg font-semibold mb-2">Process Products</h5>
          <Table
            dataSource={detailsRemarks?.process_products}
            size="small"
            columns={[
              {
                title: "Product",
                dataIndex: "product_name",
                key: "product_name",
                render: (_, record) =>
                  `${record?.product_name} (${record?.product_name_unit})`,
              },
              {
                title: "Expected Values",
                dataIndex: "value",
                key: "process_expec",
                render: (value, record) =>
                  `${record?.process_expec_min_value} - ${record?.process_expec_max_value}`,
              },
              {
                title: "Therotical Value",
                dataIndex: "process_thero_quantity",
                key: "process_thero_quantity",
              },
            ]}
            pagination={false}
            bordered
          />
        </>
      )} */}

        {detailsRemarks?.batch_product_inventory?.length > 0 && (
          <>
            <Table
              title={() => (
                <h5 className="text-lg font-semibold">Process Products</h5>
              )}
              dataSource={detailsRemarks?.batch_product_inventory}
              size="small"
              columns={[
                {
                  title: "Product",
                  dataIndex: "product_name",
                  key: "product_name",
                },
                {
                  title: "Container No",
                  dataIndex: "Container No",
                  key: "Container No",
                },
                {
                  title: "Tare Wt",
                  dataIndex: "Tare Wt.",
                  key: "Tare Wt.",
                },
                {
                  title: "Gross Wt",
                  dataIndex: "Gross Wt.",
                  key: "Gross Wt.",
                },
                {
                  title: "Net Wt",
                  dataIndex: "Net Wt.",
                  key: "Net Wt.",
                },
              ]}
              pagination={false}
              bordered
            />
          </>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: "Op No",
      dataIndex: "opNo",
      key: "opNo",
      width: "5%",
      align: "center",
    },
    {
      title: "Operation",
      dataIndex: "operation",
      key: "operation",
      width: "15%",
      render: (operation, record) =>
        record?.deviation_status ? (
          <Badge color="red" text={operation} />
        ) : (
          operation
        ),
    },
    {
      title: "Instructions",
      dataIndex: "operation",
      key: "operation",
      align: "center",
      width: "8%",
      render: (operation, record) => {
        const instructList = record?.detailsRemarks?.instrution_list;
        return (
          <Button
            type="text"
            icon={<Eye size={20} style={{ color: defaultColors.info }} />}
            onClick={() => {
              Modal.info({
                title: "Instructions",
                content:
                  instructList?.length > 0 ? (
                    <ul>
                      {instructList?.map((instruction, index) => (
                        <li key={index}>{instruction}</li>
                      ))}
                    </ul>
                  ) : (
                    <span style={{ color: defaultColors.warning }}>
                      {"There are no instructions for this operation!"}
                    </span>
                  ),
              });
            }}
          />
        );
      },
    },
    {
      title: "Equipment",
      dataIndex: "equipmentNo",
      key: "equipmentNo",
      align: "center",
      width: "20%",
      render: (equipmentNo, record) => {
        return (
          <div className="space-y-1">
            <h6 className="text-base font-semibold">{record?.equipmentNo} </h6>
            <span className="text-sm text-gray-600">
              {record?.detailsRemarks?.machine_parameters_value?.length > 0 &&
                `(${record?.detailsRemarks?.machine_parameters_value
                  ?.map((item) => item?.equipment)
                  ?.join(", ")})`}
            </span>
          </div>
        );
      },
    },
    {
      title: "From",
      dataIndex: "timeFrom",
      key: "timeFrom",
      align: "center",
      width: "12%",
      render: (timeFrom) => formatDateTime(timeFrom),
    },
    {
      title: "To",
      dataIndex: "timeTo",
      key: "timeTo",
      align: "center",
      width: "10%",
      render: (timeFrom) => formatDateTime(timeFrom),
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      align: "center",
      width: "10%",
      render: (dataIndex) => dataIndex?.split(".")[0],
    },
    {
      title: "Review",
      dataIndex: "review",
      key: "review",
      width: "8%",
      align: "center",
      render: (data, record, index) => {
        return (
          <Tooltip title="Review">
            <Button
              type="text"
              icon={<MessageCircle size={20} style={{ color: defaultColors.primary }} />}
              onClick={() => {
                Modal.info({
                  title: "Review",
                  content: (
                    <span>
                      {record?.review
                        ? record?.review
                        : "There is no Review for this operation."}
                    </span>
                  ),
                });
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Deviation",
      dataIndex: "deviation",
      key: "deviation",
      width: "10%",
      render: (_, record) => {
        return (
          <div className="p-1">
            <strong> Time :</strong> {record?.time_deviation}
          </div>
        );
      },
    },
    {
      title: "Time Deviation",
      dataIndex: "extra_time_deviation",
      key: "deviation",
      width: "10%",
      render: (_, record) => {
        return (
          <div className="p-1">
            <strong> Time :</strong> {record?.extra_time_deviation}
          </div>
        );
      },
    },
    // {
    //   title: "Performed By",
    //   dataIndex: "performed_by",
    //   key: "performed_by",
    //   width: "8%",
    //   align: "center",
    // },
  ];

  const [show, set_show] = useState([]);
  const [current, set_current] = useState("");
  const [expanded, set_expanded] = useState([]);
  return (
    <MainCard
    // title={report2 ? "Batch Details" : ""}
    // backbutton={report2 ? true : false}
    >
      {report2 && (
        <div className="flex justify-between items-center mb-4">
          <h5 className="text-lg font-semibold m-0">Batch Details</h5>
          <div className="flex gap-2">
            <Button
              type="default"
              size="middle"
              icon={<Download size={16} />}
              onClick={() => {
                get_batch_report_pdf(batch_number, (res) => {
                  base64ToFile(res?.data?.data, `${batch_number}_report`);
                });
              }}
              style={{
                borderColor: defaultColors.primary,
                color: defaultColors.primary,
              }}
            >
              Download
            </Button>
            <Button
              type="default"
              size="middle"
              icon={<ArrowLeft size={16} />}
              onClick={(e) => {
                e.preventDefault();
                navigate(-1);
              }}
              style={{
                borderColor: defaultColors.primary,
                color: defaultColors.primary,
              }}
            >
              Back
            </Button>
          </div>
        </div>
      )}
      <div style={{ padding: "10px" }}>
        <Timeline
          style={{
            width: "100%",
          }}
          mode="left"
          items={(report || report2)
            ?.filter((process) => process?.operations?.length !== 0)
            ?.map((process, index) => {
              return {
                dot: !show[index] ? (
                  <Tooltip title="View Operations">
                    <AntButton
                      type="link"
                      onClick={() => {
                        let temp = [...show];
                        temp[index] = true;
                        set_show(temp);
                        set_current(index);
                      }}
                      icon={<RightOutlined />}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="Close">
                    <AntButton
                      type="link"
                      onClick={() => {
                        let temp = [...show];
                        temp[index] = false;
                        set_show(temp);
                      }}
                      icon={<DownOutlined />}
                    />
                  </Tooltip>
                ),
                children: (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <h5 className="text-lg font-semibold m-0" style={{ color: defaultColors.secondary }}>
                        Process:
                      </h5>
                      <h5 className="text-lg font-semibold m-0">{`${process?.process_number}`}</h5>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span style={{ color: defaultColors.secondary }}>
                        {process?.employee?.length > 0
                          ? "Approved by: "
                          : "Approval Status: "}
                      </span>
                      {process?.employee?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {process?.employee?.map((item, idx) => (
                            <Tag
                              key={idx}
                              bordered
                              style={{
                                borderColor: defaultColors.primary,
                                color: defaultColors.primary,
                              }}
                            >
                              {`${item?.employee_name} - ${item?.role_name}`}
                            </Tag>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: defaultColors.warning }}>Pending</span>
                      )}
                    </div>

                    <div>
                      {show[index] &&
                        (process?.operations?.length > 0 ? (
                          <Table
                            dataSource={process?.operations?.map(
                              (item, index) => ({ ...item, key: index })
                            )}
                            size="small"
                            columns={columns}
                            pagination={false}
                            bordered
                            // Responsive: was 480px flat, anchored to a 900px reference viewport so it scales on larger/smaller screens
                            scroll={{ x: 100, y: 'calc(100vh - 420px)' }}
                            rowClassName={(record, index) => {
                              if (expanded?.includes(index)) {
                                return "col";
                              }
                            }}
                            expandable={{
                              expandedRowRender,
                              onExpandedRowsChange: (expanded) =>
                                set_expanded(expanded),
                              expandedRowClassName: () => "col2",
                            }}
                          />
                        ) : (
                          <Empty />
                        ))}
                    </div>
                  </>
                ),
              };
            })}
        />
      </div>
    </MainCard>
  );
}

export default ReportSkeleton;
